/**
 * Static storefront paths a confirmation page can present to the social pixel
 * SDKs while a Purchase is delivered. Each is a real public route with no
 * dynamic segment, so it passes the browser document-location guard.
 */
export type TenantPurchaseSafeDocumentPath = '/' | '/book' | '/guests';

/**
 * How long the masked URL stays in place after delivery resolves. The vendor
 * SDKs read `document.location` when they build the beacon, which for some of
 * them happens a tick or two after the tracking call returns.
 */
const RESTORE_DELAY_MS = 2_000;

/**
 * Temporarily replaces a payment-return URL (dynamic id + provider query) with
 * a static public path so Meta / TikTok / Snap never receive the real one,
 * then hands back a function that restores the original URL. Restoration also
 * runs eagerly when the page is hidden or unloaded, so a refresh, a copied
 * link, or a native WebView restore lands on the receipt and not on the
 * masked path.
 *
 * The existing history state is passed through unchanged so the Next.js
 * router does not treat this as a navigation.
 */
export function maskTenantPurchaseDocumentLocation(
  safeDocumentPath: TenantPurchaseSafeDocumentPath,
): () => void {
  if (isAtSafeDocumentPath(safeDocumentPath)) {
    return () => {};
  }

  const originalLocation =
    window.location.pathname + window.location.search + window.location.hash;

  try {
    window.history.replaceState(window.history.state, '', safeDocumentPath);
  } catch {
    return () => {};
  }

  let restored = false;

  const restore = () => {
    if (restored) {
      return;
    }

    restored = true;
    window.removeEventListener('pagehide', restore);
    document.removeEventListener('visibilitychange', restoreWhenHidden);

    // Someone navigated away in the meantime: leave the new URL alone.
    if (!isAtSafeDocumentPath(safeDocumentPath)) {
      return;
    }

    try {
      window.history.replaceState(window.history.state, '', originalLocation);
    } catch {
      // History access can be unavailable; the page still renders correctly.
    }
  };

  const restoreWhenHidden = () => {
    if (document.visibilityState === 'hidden') {
      restore();
    }
  };

  window.addEventListener('pagehide', restore);
  document.addEventListener('visibilitychange', restoreWhenHidden);

  return () => {
    window.setTimeout(restore, RESTORE_DELAY_MS);
  };
}

function isAtSafeDocumentPath(
  safeDocumentPath: TenantPurchaseSafeDocumentPath,
) {
  return (
    window.location.pathname === safeDocumentPath &&
    !window.location.search &&
    !window.location.hash
  );
}
